<template>
  <div class="nb-dashboard-nfe">
    <!-- PAINEL DE CONTROLE DE VENCIMENTO DO CERTIFICADO DIGITAL -->
    <div class="nb-cert-panel mb-4">
      <!-- ESTADO 1: CERTIFICADO CONFIGURADO -->
      <div class="row align-items-center" v-if="hasCertificado">
        <!-- Ícone e Informações -->
        <div class="col-lg-5 col-md-6 col-12 mb-3 mb-md-0 d-flex align-items-center">
          <div class="nb-cert-icon mr-3" :class="certIconBgClass">
            <i class="fa fa-shield" :class="certIconClass"></i>
          </div>
          <div>
            <div class="d-flex align-items-center">
              <span class="nb-cert-title">{{ certNome }}</span>
              <span class="badge ml-2" :class="certBadgeClass">{{ certStatusText }}</span>
            </div>
            <div class="nb-cert-subtitle">{{ certEmpresa }}</div>
          </div>
        </div>

        <!-- Barra de Progresso e Validade -->
        <div class="col-lg-4 col-md-6 col-12 mb-3 mb-lg-0">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span class="nb-cert-date-label">Vencimento: <strong>{{ certDataVencimento }}</strong></span>
            <span class="nb-cert-days" :class="certDaysClass">{{ certDiasTexto }}</span>
          </div>
          <div class="progress nb-cert-progress">
            <div
              class="progress-bar"
              :class="certProgressBarClass"
              role="progressbar"
              :style="{ width: certProgressPercent + '%' }"
            ></div>
          </div>
        </div>

        <!-- Ação / Botão Renovar -->
        <div class="col-lg-3 col-12 d-flex justify-content-lg-end">
          <button
            type="button"
            class="btn nb-btn-cert"
            @click="handleGerenciarCertificado"
          >
            <i class="fa fa-cog mr-1"></i> Gerenciar / Renovar
          </button>
        </div>
      </div>

      <!-- ESTADO 2: CERTIFICADO NÃO CONFIGURADO (TRATAMENTO DE AUSÊNCIA) -->
      <div class="row align-items-center" v-else>
        <div class="col-lg-7 col-md-6 col-12 mb-3 mb-md-0 d-flex align-items-center">
          <div class="nb-cert-icon cert-bg-warning mr-3">
            <i class="fa fa-exclamation-triangle text-warning"></i>
          </div>
          <div>
            <div class="d-flex align-items-center">
              <span class="nb-cert-title">Certificado Digital Não Configurado</span>
              <span class="badge badge-warning-soft ml-2">Pendente</span>
            </div>
            <div class="nb-cert-subtitle">
              Para emitir e transmitir NF-e/NFC-e na SEFAZ, vincule seu Certificado Digital A1 ou A3.
            </div>
          </div>
        </div>

        <div class="col-lg-5 col-md-6 col-12 d-flex justify-content-lg-end flex-wrap gap-2">
          <button
            type="button"
            class="btn nb-btn-primary-cert mr-2"
            @click="handleGerenciarCertificado"
          >
            <i class="fa fa-plus-circle mr-1"></i> Configurar Certificado
          </button>
          <button
            type="button"
            class="btn nb-btn-outline-cert"
            @click="handleAdquirirCertificado"
          >
            <i class="fa fa-shopping-cart mr-1"></i> Adquirir Certificado A1
          </button>
        </div>
      </div>
    </div>

    <!-- ALERTA DINÂMICO DE REJEIÇÕES SEFAZ (CONDICIONAL) -->
    <div class="nb-rejection-alert mb-4" v-if="rejeicoes && rejeicoes.length > 0">
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
        <div class="d-flex align-items-start">
          <div class="nb-alert-icon mr-3">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div>
            <div class="d-flex align-items-center flex-wrap">
              <span class="nb-alert-title">{{ rejeicoes.length }} Documento(s) com Falha de Validação SEFAZ</span>
              <span class="badge badge-danger-soft ml-2">Ação Necessária</span>
            </div>
            <p class="nb-alert-desc mb-0 mt-1">
              Notas fiscais retidas com erro de validação SEFAZ. Corrija alíquotas, somatório de itens ou duplicidades para liberar o envio.
            </p>
          </div>
        </div>
        <div>
          <button
            type="button"
            class="btn nb-btn-inspect"
            @click="showRejectionDrawer = !showRejectionDrawer"
          >
            <span>Inspecionar Rejeições</span>
            <i class="fa ml-1" :class="showRejectionDrawer ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>
      </div>

      <!-- DRAWER EXPANSÍVEL DE NOTAS REJEITADAS -->
      <div class="nb-rejection-drawer mt-3 pt-3" v-if="showRejectionDrawer">
        <div class="row">
          <div class="col-md-4 col-12 mb-2" v-for="item in rejeicoes" :key="item.codnf">
            <div class="nb-rejection-card">
              <div class="d-flex justify-content-between align-items-center">
                <span class="badge badge-danger font-mono">Rejeição {{ item.cstat }}</span>
                <span class="font-mono text-muted">NF {{ item.numero }} / S{{ item.serie }}</span>
              </div>
              <div class="nb-rejection-reason mt-2">{{ item.motivo || 'Erro de validação de regras da SEFAZ' }}</div>
              <button
                type="button"
                class="btn btn-sm btn-link nb-fix-link p-0 mt-2"
                @click="handleCorrigirNota(item.codnf)"
              >
                Corrigir e Reenviar <i class="fa fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CARDS DE FATURAMENTO (KPIs) -->
    <div class="row">
      <dash-card :params="dashboard.items[0]" :filter="filter"></dash-card>
    </div>

    <!-- GRÁFICOS LADO A LADO: 12 MESES (8 COLUNAS) E DONUT CFOP (4 COLUNAS) -->
    <div class="row">
      <dash-card :params="dashboard.items[1]" :filter="filter"></dash-card>
      <dash-card :params="dashboard.items[2]" :filter="filter"></dash-card>
    </div>
  </div>
</template>

<script>
import DashCard from './dash-card'
import moment from 'moment'
import { apiORDSpost } from '../shared/api-functions'

export default {
  name: 'dashboard-nfe',

  components: {
    DashCard,
  },

  props: {
    filterProp: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      showRejectionDrawer: false,
      rejeicoes: [],

      filter: {
        startDate: null,
        endDate: null,
        codusuario: null,
      },

      dashboard: {
        items: [
          { name: 'indicadorresumofaturamento', indicator: {}, col: 12 },
          { name: 'faturamento12meses', chart: { type: 'ColumnChart' }, col: 8, detalhes: { url: '/consultas/vendas' } },
          { name: 'distribuicaocfop', chart: { type: 'PieChart' }, col: 4, detalhes: { url: '/consultas/distribuicaocfop' } },
        ]
      },
    }
  },

  watch: {
    filterProp: {
      handler(val) {
        if (val) {
          this.filter.startDate = val.startDate
          this.filter.endDate = val.endDate
          this.filter.codusuario = val.codusuario
          this.loadRejeicoes()
        }
      },
      deep: true,
      immediate: true
    }
  },

  computed: {
    sessao() {
      return this.$store.getters.getSessao
    },

    filialList() {
      return this.$store.getters.getFilialList || []
    },

    certData() {
      // 1. Fonte da verdade: Lista de filiais do tenant ativo
      if (this.filialList && this.filialList.length > 0) {
        const f = this.filialList[0]
        // Se a filial indica cert_pendente === 'S' ou não tem codcertificado válido: NÃO TEM CERTIFICADO!
        if (f.cert_pendente === 'S' || !f.codcertificado || f.codcertificado === 0) {
          return null
        }

        const sessaoCert = (this.sessao && Array.isArray(this.sessao.certificado))
          ? this.sessao.certificado.find(item => item && (item.codfil == f.codfil || item.codcertificado == f.codcertificado))
          : null

        const tpcertificado = f.cert_tpcertificado || f.tpcertificado || (sessaoCert && sessaoCert.tpcertificado) || (f.cert_filename ? 'A1' : 'A3')
        const dtval = f.cert_dtval || (sessaoCert && sessaoCert.dtval)

        return {
          codcertificado: f.codcertificado,
          codfil: f.codfil,
          dtval: dtval,
          nome: f.cert_nome || (sessaoCert && sessaoCert.nome),
          razao: f.razao || f.nome,
          validade: dtval ? moment(dtval).diff(moment().startOf('day'), 'days') : (sessaoCert && typeof sessaoCert.validade === 'number' ? sessaoCert.validade : 0),
          filename: f.cert_filename,
          tpcertificado: tpcertificado
        }
      }

      // 2. Fallback de sessão (quando filialList ainda não carregou ou está vazia)
      if (this.sessao && this.sessao.certificado && Array.isArray(this.sessao.certificado) && this.sessao.certificado.length > 0) {
        const c = this.sessao.certificado.find(item => item && item.codcertificado > 0 && item.dtval)
        if (c && c.codcertificado > 0 && c.dtval) {
          if (this.filialList.length > 0 && this.filialList[0].cert_pendente === 'S') {
            return null
          }
          return {
            codcertificado: c.codcertificado,
            codfil: c.codfil,
            dtval: c.dtval,
            nome: c.nome,
            razao: this.sessao.empresanome || this.sessao.empresa_nome,
            validade: typeof c.validade === 'number' ? c.validade : moment(c.dtval).diff(moment().startOf('day'), 'days'),
            filename: c.filename,
            tpcertificado: c.tpcertificado || (c.filename ? 'A1' : 'A3')
          }
        }
      }

      return null
    },

    hasCertificado() {
      if (this.filialList && this.filialList.length > 0) {
        const f = this.filialList[0]
        if (f.cert_pendente === 'S' || !f.codcertificado || f.codcertificado === 0) {
          return false
        }
      }
      return Boolean(this.certData && this.certData.codcertificado && (this.certData.dtval || this.certData.codcertificado > 0))
    },

    isCertA3() {
      return Boolean(this.certData && this.certData.tpcertificado === 'A3')
    },

    certNome() {
      if (this.certData && this.certData.nome) {
        return this.certData.nome
      }
      if (this.isCertA3) {
        return 'Certificado Digital A3'
      }
      return 'Certificado Digital A1 (e-CNPJ)'
    },

    certEmpresa() {
      if (this.certData && this.certData.razao) {
        return this.certData.razao
      }
      if (this.sessao && (this.sessao.empresanome || this.sessao.empresa_nome)) {
        return (this.sessao.empresanome || this.sessao.empresa_nome) + ' • ICP-Brasil'
      }
      return 'Nota Brasil • ICP-Brasil'
    },

    certDataVencimento() {
      if (this.certData && this.certData.dtval) {
        return moment(this.certData.dtval).format('DD/MM/YYYY')
      }
      return '--/--/----'
    },

    certDiasRestantes() {
      if (this.certData && typeof this.certData.validade === 'number') {
        return this.certData.validade
      }
      return 0
    },

    certDiasTexto() {
      const dias = this.certDiasRestantes
      if (dias < 0) return 'Vencido há ' + Math.abs(dias) + ' dias'
      if (dias === 0) return 'Vence hoje'
      return 'Vence em ' + dias + ' dias'
    },

    certDaysClass() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'text-danger'
      if (dias <= 30) return 'text-warning'
      return 'text-success'
    },

    certStatusText() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'Vencido'
      if (dias <= 30) return 'Vencimento Próximo'
      return 'Ativo & Válido'
    },

    certBadgeClass() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'badge-danger-soft'
      if (dias <= 30) return 'badge-warning-soft'
      return 'badge-success-soft'
    },

    certIconBgClass() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'cert-bg-danger'
      if (dias <= 30) return 'cert-bg-warning'
      return 'cert-bg-success'
    },

    certIconClass() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'text-danger'
      if (dias <= 30) return 'text-warning'
      return 'text-success'
    },

    certProgressBarClass() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 'bg-danger'
      if (dias <= 30) return 'bg-warning'
      return 'bg-success'
    },

    certProgressPercent() {
      const dias = this.certDiasRestantes
      if (dias <= 0) return 100
      return Math.min(100, Math.max(10, Math.round((dias / 365) * 100)))
    }
  },

  methods: {
    updateQuery() {
      this.$router.replace({ query: { data_inicio: this.filter.startDate, data_termino: this.filter.endDate } })
    },

    handleGerenciarCertificado() {
      this.$router.push('/filiallist/certificado')
    },

    handleAdquirirCertificado() {
      window.open('https://notabrasil.com.br/certificado-digital', '_blank')
    },

    handleCorrigirNota(codnf) {
      this.$router.push('/nf/' + codnf)
    },

    async loadRejeicoes() {
      try {
        const filtro = {
          data_inicio: this.filter.startDate,
          data_termino: this.filter.endDate
        }
        const res = await apiORDSpost(this.$store, 'dashboard/element/notasrejeitadas', filtro, 'post')
        if (res && res.rejeitadas) {
          this.rejeicoes = res.rejeitadas
        } else {
          this.rejeicoes = []
        }
      } catch (e) {
        this.rejeicoes = []
      }
    },

    async loadData() {
      await Promise.all([
        this.$store.dispatch('loadFilial', {}),
        this.$store.dispatch('loadSessao', this)
      ])
      this.loadRejeicoes()
    }
  },

  mounted() {
    this.loadData()
  },

  created() {
    if (!this.filterProp && this.$route.query.data_inicio) {
      this.filter.startDate = this.$firstDay(this.$route.query.data_inicio)
      this.filter.endDate = this.$lastDay(this.$route.query.data_termino)
    }
  }
}
</script>

<style scoped>
.nb-dashboard-nfe {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0b1c30;
}

/* PAINEL DE CERTIFICADO DIGITAL */
.nb-cert-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.nb-cert-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.cert-bg-success { background: #ecfdf5; }
.cert-bg-warning { background: #fffbeb; }
.cert-bg-danger { background: #fef2f2; }

.nb-cert-title {
  font-size: 14px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-cert-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.nb-cert-date-label {
  font-size: 12px;
  color: #475569;
}

.nb-cert-days {
  font-size: 12px;
  font-weight: 700;
}

.nb-cert-progress {
  height: 6px;
  border-radius: 10px;
  background: #f1f5f9;
}

.nb-btn-cert {
  background: #f8fafc;
  color: #004e94;
  border: 1px solid #cbd5e1;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-cert:hover {
  background: #eff4ff;
  border-color: #004e94;
}

.nb-btn-primary-cert {
  background: #004e94;
  color: #ffffff;
  border: 1px solid #004e94;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-primary-cert:hover {
  background: #00376c;
  color: #ffffff;
}

.nb-btn-outline-cert {
  background: #ffffff;
  color: #0b1c30;
  border: 1px solid #cbd5e1;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-outline-cert:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* BADGES */
.badge-success-soft {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.badge-warning-soft {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.badge-danger-soft {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

/* ALERTA DE REJEIÇÕES */
.nb-rejection-alert {
  background: #ffffff;
  border: 1px solid #fecaca;
  border-left: 4px solid #ef4444;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.nb-alert-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fee2e2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.nb-alert-title {
  font-size: 14px;
  font-weight: 700;
  color: #0b1c30;
}

.nb-alert-desc {
  font-size: 12.5px;
  color: #64748b;
}

.nb-btn-inspect {
  background: #fee2e2;
  color: #b91c1c;
  border: none;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  transition: all 0.15s ease;
}

.nb-btn-inspect:hover {
  background: #fecaca;
}

.nb-rejection-drawer {
  border-top: 1px dashed #fecaca;
}

.nb-rejection-card {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 12px;
}

.nb-rejection-reason {
  font-size: 11.5px;
  color: #334155;
  line-height: 1.3;
}

.nb-fix-link {
  font-size: 11.5px;
  font-weight: 700;
  color: #004e94;
  text-decoration: none !important;
}

.nb-fix-link:hover {
  color: #00376c;
  text-decoration: underline !important;
}
</style>






// WEBPACK FOOTER //
// src/dashboards/dashboard-nfe.vue